import * as THREE from 'three';
import { Player } from '../game/Player';
import { MaskManager, MaskType } from '../masks/MaskManager';
import { AresMask } from '../masks/AresMask';
import { PuzzleElement } from '../puzzles/PuzzleElement';
import { TimedDoor } from '../puzzles/TimedDoor';
import { PressurePlate } from '../puzzles/PressurePlate';
import { BreakableWall } from '../puzzles/BreakableWall';
import { WaterZone } from '../puzzles/WaterZone';
import { GuardianStatue } from '../puzzles/GuardianStatue';
import { MaskPickup } from '../puzzles/MaskPickup';
import { HiddenPlatform } from '../puzzles/HiddenPlatform';
import { ExitPortal } from '../puzzles/ExitPortal';
import type { Game } from '../game/Game';

interface LevelConfig {
  name: string;
  playerStart: THREE.Vector3;
  ambientColor: number;
  fogColor: number;
  build: (manager: LevelManager) => void;
}

export class LevelManager {
  private scene: THREE.Scene;
  private player: Player;
  private maskManager: MaskManager;
  private game: Game | null = null;
  
  private currentLevel: number = 0;
  private puzzleElements: PuzzleElement[] = [];
  private collisionBoxes: THREE.Box3[] = [];
  private waterZones: WaterZone[] = [];
  private breakableWalls: BreakableWall[] = [];
  private timedDoors: TimedDoor[] = [];
  private guardians: GuardianStatue[] = [];
  private hiddenPlatforms: HiddenPlatform[] = [];
  private maskPickups: MaskPickup[] = [];
  
  private gameComplete: boolean = false;
  private levelMeshes: THREE.Object3D[] = [];
  
  // Drowning mechanic
  private drowningTimer: number = 0;
  private maxDrowningTime: number = 3; // Seconds before death
  
  private levels: LevelConfig[] = [];
  
  constructor(scene: THREE.Scene, player: Player, maskManager: MaskManager) {
    this.scene = scene;
    this.player = player;
    this.maskManager = maskManager;
    
    this.setupLevels();
    this.setupAresMaskCallback();
  }
  
  private setupAresMaskCallback(): void {
    const aresMask = this.maskManager['masks'].get(MaskType.ARES) as AresMask;
    if (aresMask) {
      aresMask.onBreakAttempt = (position, direction) => {
        for (const wall of this.breakableWalls) {
          if (wall.tryBreak(position, direction)) {
            return true;
          }
        }
        return false;
      };
    }
  }
  
  private setupLevels(): void {
    // Level 1: Hall of Beginnings - Tutorial
    this.levels.push({
      name: 'Hall of Beginnings',
      playerStart: new THREE.Vector3(0, 0, -45),
      ambientColor: 0x404060,
      fogColor: 0x1a1a2e,
      build: (manager) => this.buildChamber1(manager)
    });
    
    // Level 2: The Racing Corridors - Hermes
    this.levels.push({
      name: 'The Racing Corridors',
      playerStart: new THREE.Vector3(0, 0, -65),
      ambientColor: 0x605040,
      fogColor: 0x2a1a0e,
      build: (manager) => this.buildChamber2(manager)
    });
    
    // Level 3: The Sunken Sanctuary - Poseidon
    this.levels.push({
      name: 'The Sunken Sanctuary',
      playerStart: new THREE.Vector3(0, 1, -60),
      ambientColor: 0x204060,
      fogColor: 0x0a1a2e,
      build: (manager) => this.buildChamber3(manager)
    });
    
    // Level 4: The Shattered Gallery - Ares
    this.levels.push({
      name: 'The Shattered Gallery',
      playerStart: new THREE.Vector3(0, 0, -35),
      ambientColor: 0x602020,
      fogColor: 0x1a0a0a,
      build: (manager) => this.buildChamber4(manager)
    });
    
    // Level 5: The Shadow Sanctum - Hades & Apollo
    this.levels.push({
      name: 'The Shadow Sanctum',
      playerStart: new THREE.Vector3(0, 0, -55),
      ambientColor: 0x301030,
      fogColor: 0x0a0510,
      build: (manager) => this.buildChamber5(manager)
    });
  }
  
  public loadLevel(levelIndex: number): void {
    this.currentLevel = levelIndex;
    this.clearLevel();
    
    if (levelIndex >= this.levels.length) {
      this.gameComplete = true;
      return;
    }
    
    const level = this.levels[levelIndex];
    
    // Set atmosphere
    this.scene.background = new THREE.Color(level.fogColor);
    this.scene.fog = new THREE.Fog(level.fogColor, 5, 40);
    
    // Add ambient light
    const ambient = new THREE.AmbientLight(level.ambientColor, 0.4);
    this.scene.add(ambient);
    this.levelMeshes.push(ambient);
    
    // Add directional light
    const directional = new THREE.DirectionalLight(0xffffff, 0.6);
    directional.position.set(10, 20, 10);
    directional.castShadow = true;
    directional.shadow.mapSize.width = 2048;
    directional.shadow.mapSize.height = 2048;
    directional.shadow.camera.near = 0.5;
    directional.shadow.camera.far = 50;
    directional.shadow.camera.left = -20;
    directional.shadow.camera.right = 20;
    directional.shadow.camera.top = 20;
    directional.shadow.camera.bottom = -20;
    this.scene.add(directional);
    this.levelMeshes.push(directional);
    
    // Build level geometry
    level.build(this);
    
    // Move player to start
    this.player.teleportTo(level.playerStart.x, level.playerStart.y, level.playerStart.z);
  }
  
  private clearLevel(): void {
    // Remove all level meshes
    for (const mesh of this.levelMeshes) {
      this.scene.remove(mesh);
    }
    this.levelMeshes = [];
    
    // Remove puzzle elements
    for (const element of this.puzzleElements) {
      this.scene.remove(element.getMesh());
    }
    this.puzzleElements = [];
    this.collisionBoxes = [];
    this.waterZones = [];
    this.breakableWalls = [];
    this.timedDoors = [];
    this.guardians = [];
    this.hiddenPlatforms = [];
    this.maskPickups = [];
    this.drowningTimer = 0;
  }
  
  public update(deltaTime: number): void {
    // Update all puzzle elements
    for (const element of this.puzzleElements) {
      element.update(deltaTime, this.player.position);
    }
    
    // Update guardians with invisibility check
    for (const guardian of this.guardians) {
      guardian.update(deltaTime, this.player.position, this.player.isInvisible);
    }
    
    // Update hidden platforms with light check
    for (const platform of this.hiddenPlatforms) {
      platform.reveal(this.player.hasLightAura);
    }
    
    // Check mask pickups
    for (const pickup of this.maskPickups) {
      if (!pickup.isCollectedState() && pickup.tryCollect(this.player.position)) {
        this.maskManager.unlockMask(pickup.maskType);
      }
    }
    
    // Drowning mechanic - player in water without Poseidon mask
    if (this.player.isInWater && !this.player.canBreatheUnderwater) {
      this.drowningTimer += deltaTime;
      if (this.drowningTimer >= this.maxDrowningTime) {
        // Reset player to level start
        const level = this.levels[this.currentLevel];
        this.player.teleportTo(level.playerStart.x, level.playerStart.y, level.playerStart.z);
        this.drowningTimer = 0;
      }
    } else {
      this.drowningTimer = Math.max(0, this.drowningTimer - deltaTime * 2); // Recover when out of water
    }
  }
  
  public checkCollision(position: THREE.Vector3, radius: number): THREE.Vector3 | null {
    let correctedPosition = position.clone();
    let hasCollision = false;
    
    for (const box of this.collisionBoxes) {
      if (this.sphereIntersectsBox(position, radius, box)) {
        hasCollision = true;
        correctedPosition = this.pushOutOfBox(correctedPosition, radius, box);
      }
    }
    
    // Check breakable walls
    for (const wall of this.breakableWalls) {
      const wallBox = wall.getCollisionBox();
      if (wallBox && this.sphereIntersectsBox(correctedPosition, radius, wallBox)) {
        hasCollision = true;
        correctedPosition = this.pushOutOfBox(correctedPosition, radius, wallBox);
      }
    }
    
    // Check timed doors
    for (const door of this.timedDoors) {
      const doorBox = door.getCollisionBox();
      if (doorBox && this.sphereIntersectsBox(correctedPosition, radius, doorBox)) {
        hasCollision = true;
        correctedPosition = this.pushOutOfBox(correctedPosition, radius, doorBox);
      }
    }
    
    // Check hidden platforms (only when revealed)
    for (const platform of this.hiddenPlatforms) {
      const platBox = platform.getCollisionBox();
      if (platBox && this.sphereIntersectsBox(correctedPosition, radius, platBox)) {
        // Platforms should support the player, not block horizontally
        if (position.y > platBox.max.y - 0.5) {
          correctedPosition.y = Math.max(correctedPosition.y, platBox.max.y + 0.1);
        }
      }
    }
    
    return hasCollision ? correctedPosition : null;
  }
  
  private sphereIntersectsBox(center: THREE.Vector3, radius: number, box: THREE.Box3): boolean {
    const closestPoint = new THREE.Vector3();
    box.clampPoint(center, closestPoint);
    return center.distanceTo(closestPoint) < radius;
  }
  
  private pushOutOfBox(position: THREE.Vector3, radius: number, box: THREE.Box3): THREE.Vector3 {
    const result = position.clone();
    const center = new THREE.Vector3();
    box.getCenter(center);
    
    // Find the axis with smallest penetration
    const dx1 = box.max.x - position.x + radius;
    const dx2 = position.x - box.min.x + radius;
    const dz1 = box.max.z - position.z + radius;
    const dz2 = position.z - box.min.z + radius;
    
    const minDx = Math.min(dx1, dx2);
    const minDz = Math.min(dz1, dz2);
    
    if (minDx < minDz) {
      if (dx1 < dx2) {
        result.x = box.max.x + radius;
      } else {
        result.x = box.min.x - radius;
      }
    } else {
      if (dz1 < dz2) {
        result.z = box.max.z + radius;
      } else {
        result.z = box.min.z - radius;
      }
    }
    
    return result;
  }
  
  public checkWater(position: THREE.Vector3): boolean {
    for (const water of this.waterZones) {
      if (water.containsPoint(position)) {
        return true;
      }
    }
    return false;
  }
  
  public interact(position: THREE.Vector3, camera: THREE.Camera): void {
    // Raycast to find interactive objects
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    raycaster.far = 3;
    
    // Check for interactions
    // This could be expanded for levers, buttons, etc.
  }
  
  public isGameComplete(): boolean {
    return this.gameComplete;
  }
  
  public nextLevel(): void {
    this.loadLevel(this.currentLevel + 1);
    // Show level name hint
    if (this.game && this.currentLevel < this.levels.length) {
      this.game.showHint(this.getCurrentLevelName(), 4000);
    }
  }
  
  public setGame(game: Game): void {
    this.game = game;
  }
  
  public getCurrentLevelName(): string {
    if (this.currentLevel < this.levels.length) {
      return `Chapter ${this.currentLevel + 1}: ${this.levels[this.currentLevel].name}`;
    }
    return '';
  }
  
  public getDrowningProgress(): number {
    return this.drowningTimer / this.maxDrowningTime;
  }
  
  // Helper methods for building levels
  public addFloor(width: number, depth: number, y: number = 0, color: number = 0x3a3a4a): void {
    const geometry = new THREE.PlaneGeometry(width, depth);
    const material = new THREE.MeshStandardMaterial({ 
      color: color,
      roughness: 0.8,
      metalness: 0.2
    });
    const floor = new THREE.Mesh(geometry, material);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = y;
    floor.receiveShadow = true;
    this.scene.add(floor);
    this.levelMeshes.push(floor);
  }
  
  public addWall(
    position: THREE.Vector3, 
    size: THREE.Vector3, 
    color: number = 0x5a5a6a
  ): void {
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    const material = new THREE.MeshStandardMaterial({ 
      color: color,
      roughness: 0.9,
      metalness: 0.1
    });
    const wall = new THREE.Mesh(geometry, material);
    wall.position.copy(position);
    wall.position.y += size.y / 2;
    wall.castShadow = true;
    wall.receiveShadow = true;
    this.scene.add(wall);
    this.levelMeshes.push(wall);
    
    // Add collision box
    const box = new THREE.Box3().setFromObject(wall);
    this.collisionBoxes.push(box);
  }
  
  public addColumn(x: number, z: number, height: number = 5): void {
    const geometry = new THREE.CylinderGeometry(0.4, 0.5, height, 8);
    const material = new THREE.MeshStandardMaterial({ 
      color: 0xdcdcdc,
      roughness: 0.6,
      metalness: 0.2
    });
    const column = new THREE.Mesh(geometry, material);
    column.position.set(x, height / 2, z);
    column.castShadow = true;
    column.receiveShadow = true;
    this.scene.add(column);
    this.levelMeshes.push(column);
    
    // Add collision
    const box = new THREE.Box3().setFromObject(column);
    this.collisionBoxes.push(box);
  }
  
  public addPuzzleElement(element: PuzzleElement): void {
    this.puzzleElements.push(element);
    this.scene.add(element.getMesh());
  }
  
  public addTimedDoor(position: THREE.Vector3, duration: number = 3, width: number = 3): TimedDoor {
    const door = new TimedDoor(position, duration, 3, width);
    this.addPuzzleElement(door);
    this.timedDoors.push(door);
    return door;
  }
  
  public addPressurePlate(
    position: THREE.Vector3, 
    onActivate?: () => void,
    onDeactivate?: () => void
  ): PressurePlate {
    const plate = new PressurePlate(position, false, onActivate, onDeactivate);
    this.addPuzzleElement(plate);
    return plate;
  }
  
  public addBreakableWall(position: THREE.Vector3, size?: THREE.Vector3): BreakableWall {
    const wall = new BreakableWall(position, this.scene, size);
    this.addPuzzleElement(wall);
    this.breakableWalls.push(wall);
    return wall;
  }
  
  public addWaterZone(position: THREE.Vector3, size: THREE.Vector3): WaterZone {
    const water = new WaterZone(position, size);
    this.addPuzzleElement(water);
    this.waterZones.push(water);
    return water;
  }
  
  public addGuardian(position: THREE.Vector3, facing: THREE.Vector3): GuardianStatue {
    const guardian = new GuardianStatue(position, facing);
    this.addPuzzleElement(guardian);
    this.guardians.push(guardian);
    return guardian;
  }
  
  public addMaskPickup(position: THREE.Vector3, maskType: MaskType): MaskPickup {
    const pickup = new MaskPickup(position, maskType);
    this.addPuzzleElement(pickup);
    this.maskPickups.push(pickup);
    return pickup;
  }
  
  public addHiddenPlatform(position: THREE.Vector3, size: THREE.Vector3): HiddenPlatform {
    const platform = new HiddenPlatform(position, size);
    this.addPuzzleElement(platform);
    this.hiddenPlatforms.push(platform);
    return platform;
  }
  
  public addExitPortal(position: THREE.Vector3): ExitPortal {
    const portal = new ExitPortal(position, () => this.nextLevel());
    this.addPuzzleElement(portal);
    return portal;
  }
  
  // ==================== LEVEL BUILDERS ====================
  
  private buildChamber1(manager: LevelManager): void {
    // Tutorial level - longer corridor leading to Hermes mask
    const corridorWidth = 12;
    const corridorLength = 100;
    
    // Floor
    manager.addFloor(corridorWidth + 4, corridorLength + 20);
    
    // Main corridor walls - completely enclosed
    manager.addWall(new THREE.Vector3(-corridorWidth/2 - 1, 0, 0), new THREE.Vector3(2, 6, corridorLength + 20));
    manager.addWall(new THREE.Vector3(corridorWidth/2 + 1, 0, 0), new THREE.Vector3(2, 6, corridorLength + 20));
    manager.addWall(new THREE.Vector3(0, 0, -corridorLength/2 - 8), new THREE.Vector3(corridorWidth + 6, 6, 2));
    manager.addWall(new THREE.Vector3(0, 0, corridorLength/2 + 8), new THREE.Vector3(corridorWidth + 6, 6, 2));
    
    // Decorative columns along corridor
    for (let z = -40; z <= 40; z += 15) {
      manager.addColumn(-4, z, 6);
      manager.addColumn(4, z, 6);
    }
    
    // Hermes mask at the far end
    manager.addMaskPickup(new THREE.Vector3(0, 1.5, 45), MaskType.HERMES);
    
    // Exit portal behind the mask
    manager.addExitPortal(new THREE.Vector3(0, 0.1, 52));
  }
  
  private buildChamber2(manager: LevelManager): void {
    // Racing corridors - MUST use Hermes time slow to pass timed doors
    const corridorWidth = 10;
    const corridorLength = 150;
    
    // Floor
    manager.addFloor(corridorWidth + 4, corridorLength + 20);
    
    // Walls - narrow corridor, no way around
    manager.addWall(new THREE.Vector3(-corridorWidth/2 - 1, 0, 0), new THREE.Vector3(2, 6, corridorLength + 20));
    manager.addWall(new THREE.Vector3(corridorWidth/2 + 1, 0, 0), new THREE.Vector3(2, 6, corridorLength + 20));
    manager.addWall(new THREE.Vector3(0, 0, -corridorLength/2 - 8), new THREE.Vector3(corridorWidth + 6, 6, 2));
    manager.addWall(new THREE.Vector3(0, 0, corridorLength/2 + 8), new THREE.Vector3(corridorWidth + 6, 6, 2));
    
    // Create wall sections with doors that span the ENTIRE corridor width
    // These create checkpoints that REQUIRE the timed doors to pass
    
    // First gate section
    manager.addWall(new THREE.Vector3(-corridorWidth/2 + 1.5, 0, -40), new THREE.Vector3(3, 6, 2));
    manager.addWall(new THREE.Vector3(corridorWidth/2 - 1.5, 0, -40), new THREE.Vector3(3, 6, 2));
    const door1 = manager.addTimedDoor(new THREE.Vector3(0, 2.5, -40), 1.8, corridorWidth - 4);
    
    // Second gate section  
    manager.addWall(new THREE.Vector3(-corridorWidth/2 + 1.5, 0, 0), new THREE.Vector3(3, 6, 2));
    manager.addWall(new THREE.Vector3(corridorWidth/2 - 1.5, 0, 0), new THREE.Vector3(3, 6, 2));
    const door2 = manager.addTimedDoor(new THREE.Vector3(0, 2.5, 0), 1.5, corridorWidth - 4);
    
    // Third gate section - very short timer, requires time slow
    manager.addWall(new THREE.Vector3(-corridorWidth/2 + 1.5, 0, 40), new THREE.Vector3(3, 6, 2));
    manager.addWall(new THREE.Vector3(corridorWidth/2 - 1.5, 0, 40), new THREE.Vector3(3, 6, 2));
    const door3 = manager.addTimedDoor(new THREE.Vector3(0, 2.5, 40), 1.0, corridorWidth - 4);
    
    // Pressure plates far from doors - requires speed
    manager.addPressurePlate(new THREE.Vector3(0, 0.05, -55), () => door1.trigger());
    manager.addPressurePlate(new THREE.Vector3(0, 0.05, -20), () => door2.trigger());
    manager.addPressurePlate(new THREE.Vector3(0, 0.05, 20), () => door3.trigger());
    
    // Poseidon mask reward after all gates
    manager.addMaskPickup(new THREE.Vector3(0, 1.5, 60), MaskType.POSEIDON);
    
    // Exit portal
    manager.addExitPortal(new THREE.Vector3(0, 0.1, 70));
  }
  
  private buildChamber3(manager: LevelManager): void {
    // Sunken sanctuary - MUST use Poseidon to swim through water
    const roomWidth = 50;
    const roomLength = 140;
    
    // Main floor spanning entire level
    manager.addFloor(roomWidth + 10, roomLength + 20, -5, 0x2a3a4a);
    
    // Outer walls - fully enclosed
    manager.addWall(new THREE.Vector3(-roomWidth/2 - 3, 0, 0), new THREE.Vector3(2, 10, roomLength));
    manager.addWall(new THREE.Vector3(roomWidth/2 + 3, 0, 0), new THREE.Vector3(2, 10, roomLength));
    manager.addWall(new THREE.Vector3(0, 0, -roomLength/2 - 3), new THREE.Vector3(roomWidth + 10, 10, 2));
    manager.addWall(new THREE.Vector3(0, 0, roomLength/2 + 3), new THREE.Vector3(roomWidth + 10, 10, 2));
    
    // === STARTING AREA (dry) ===
    // Elevated starting platform
    manager.addWall(new THREE.Vector3(0, -0.5, -60), new THREE.Vector3(20, 1, 20), 0x5a5a6a);
    
    // === WATER SECTION ===
    // Water pool - player must swim through
    manager.addWaterZone(
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(roomWidth - 4, 8, 80)
    );
    
    // Underwater barriers - forces player to dive deep
    // First underwater wall with gap at bottom
    manager.addWall(new THREE.Vector3(-12, 1, -20), new THREE.Vector3(20, 6, 2), 0x3a4a5a);
    manager.addWall(new THREE.Vector3(12, 1, -20), new THREE.Vector3(20, 6, 2), 0x3a4a5a);
    
    // Second underwater wall with gap at bottom
    manager.addWall(new THREE.Vector3(-12, 1, 10), new THREE.Vector3(20, 6, 2), 0x3a4a5a);
    manager.addWall(new THREE.Vector3(12, 1, 10), new THREE.Vector3(20, 6, 2), 0x3a4a5a);
    
    // Ares mask deep underwater in the middle
    manager.addMaskPickup(new THREE.Vector3(0, -3, -5), MaskType.ARES);
    
    // === EXIT AREA (dry) ===
    // Elevated exit platform - above water level
    manager.addWall(new THREE.Vector3(0, -0.5, 55), new THREE.Vector3(24, 1, 24), 0x5a5a6a);
    
    // Ramp up from water to exit platform
    manager.addWall(new THREE.Vector3(0, -1.5, 45), new THREE.Vector3(16, 2, 10), 0x4a4a5a);
    
    // Exit portal on the dry platform
    manager.addExitPortal(new THREE.Vector3(0, 0.6, 60));
  }
  
  private buildChamber4(manager: LevelManager): void {
    // Shattered gallery - MUST use Ares to break walls
    const roomSize = 80;
    
    // Floor
    manager.addFloor(roomSize + 10, roomSize + 10);
    
    // Outer walls - completely enclosed
    manager.addWall(new THREE.Vector3(-roomSize/2 - 3, 0, 0), new THREE.Vector3(2, 6, roomSize + 10));
    manager.addWall(new THREE.Vector3(roomSize/2 + 3, 0, 0), new THREE.Vector3(2, 6, roomSize + 10));
    manager.addWall(new THREE.Vector3(0, 0, -roomSize/2 - 3), new THREE.Vector3(roomSize + 10, 6, 2));
    manager.addWall(new THREE.Vector3(0, 0, roomSize/2 + 3), new THREE.Vector3(roomSize + 10, 6, 2));
    
    // Create a maze-like structure with breakable walls as the ONLY way through
    
    // First barrier - complete wall with breakable section
    manager.addWall(new THREE.Vector3(-25, 0, -25), new THREE.Vector3(20, 6, 2));
    manager.addWall(new THREE.Vector3(25, 0, -25), new THREE.Vector3(20, 6, 2));
    manager.addBreakableWall(new THREE.Vector3(0, 2, -25), new THREE.Vector3(12, 5, 2));
    
    // Second barrier - different position
    manager.addWall(new THREE.Vector3(-30, 0, 0), new THREE.Vector3(25, 6, 2));
    manager.addWall(new THREE.Vector3(20, 0, 0), new THREE.Vector3(35, 6, 2));
    manager.addBreakableWall(new THREE.Vector3(-10, 2, 0), new THREE.Vector3(12, 5, 2));
    
    // Third barrier
    manager.addWall(new THREE.Vector3(-20, 0, 25), new THREE.Vector3(35, 6, 2));
    manager.addWall(new THREE.Vector3(30, 0, 25), new THREE.Vector3(15, 6, 2));
    manager.addBreakableWall(new THREE.Vector3(15, 2, 25), new THREE.Vector3(12, 5, 2));
    
    // Side walls to prevent going around
    manager.addWall(new THREE.Vector3(-35, 0, -12), new THREE.Vector3(2, 6, 28));
    manager.addWall(new THREE.Vector3(35, 0, 12), new THREE.Vector3(2, 6, 28));
    
    // Hades mask in the final section
    manager.addMaskPickup(new THREE.Vector3(0, 1.5, 35), MaskType.HADES);
    
    // Exit portal
    manager.addExitPortal(new THREE.Vector3(0, 0.1, 42));
  }
  
  private buildChamber5(manager: LevelManager): void {
    // Shadow sanctum - guardians + hidden paths
    const roomWidth = 80;
    const roomLength = 120;
    
    // Floor
    manager.addFloor(roomWidth + 10, roomLength + 20, 0, 0x2a2a3a);
    
    // Outer walls
    manager.addWall(new THREE.Vector3(-roomWidth/2 - 3, 0, 0), new THREE.Vector3(2, 8, roomLength + 20));
    manager.addWall(new THREE.Vector3(roomWidth/2 + 3, 0, 0), new THREE.Vector3(2, 8, roomLength + 20));
    manager.addWall(new THREE.Vector3(0, 0, -roomLength/2 - 8), new THREE.Vector3(roomWidth + 10, 8, 2));
    manager.addWall(new THREE.Vector3(0, 0, roomLength/2 + 8), new THREE.Vector3(roomWidth + 10, 8, 2));
    
    // Guardian gauntlet - narrow corridor with guardians looking at each other
    // IMPOSSIBLE to pass without Hades invisibility
    
    // Create narrow passage with guardians
    manager.addWall(new THREE.Vector3(-8, 0, -20), new THREE.Vector3(2, 6, 40));
    manager.addWall(new THREE.Vector3(8, 0, -20), new THREE.Vector3(2, 6, 40));
    
    // Guardians facing the passage - no way to sneak past
    const guardian1 = manager.addGuardian(new THREE.Vector3(-12, 0, -35), new THREE.Vector3(1, 0, 0.5));
    const guardian2 = manager.addGuardian(new THREE.Vector3(12, 0, -35), new THREE.Vector3(-1, 0, 0.5));
    const guardian3 = manager.addGuardian(new THREE.Vector3(-12, 0, -15), new THREE.Vector3(1, 0, 0));
    const guardian4 = manager.addGuardian(new THREE.Vector3(12, 0, -15), new THREE.Vector3(-1, 0, 0));
    const guardian5 = manager.addGuardian(new THREE.Vector3(0, 0, 5), new THREE.Vector3(0, 0, -1));
    
    // All guardians reset player
    const resetPlayer = () => {
      if (!this.player.isInvisible) {
        this.player.teleportTo(0, 0, -55);
      }
    };
    guardian1.setDetectionCallback(resetPlayer);
    guardian2.setDetectionCallback(resetPlayer);
    guardian3.setDetectionCallback(resetPlayer);
    guardian4.setDetectionCallback(resetPlayer);
    guardian5.setDetectionCallback(resetPlayer);
    
    // Apollo mask after guardian section but before pit
    manager.addMaskPickup(new THREE.Vector3(0, 1.5, 15), MaskType.APOLLO);
    
    // Pit section - hidden platforms required
    // Create a pit that player falls into
    manager.addWall(new THREE.Vector3(-30, -3, 35), new THREE.Vector3(2, 6, 30), 0x1a1a2a);
    manager.addWall(new THREE.Vector3(30, -3, 35), new THREE.Vector3(2, 6, 30), 0x1a1a2a);
    
    // Blocking wall - must use hidden platforms to get over
    manager.addWall(new THREE.Vector3(-35, 0, 25), new THREE.Vector3(30, 6, 2));
    manager.addWall(new THREE.Vector3(35, 0, 25), new THREE.Vector3(30, 6, 2));
    // Gap in the middle but no floor - need hidden platforms
    
    // Hidden platforms across the gap (only visible with Apollo)
    manager.addHiddenPlatform(new THREE.Vector3(-5, 1, 30), new THREE.Vector3(6, 1, 6));
    manager.addHiddenPlatform(new THREE.Vector3(5, 1.5, 38), new THREE.Vector3(6, 1, 6));
    manager.addHiddenPlatform(new THREE.Vector3(-3, 2, 46), new THREE.Vector3(6, 1, 6));
    manager.addHiddenPlatform(new THREE.Vector3(0, 2.5, 54), new THREE.Vector3(8, 1, 8));
    
    // Final exit portal
    const finalPortal = manager.addExitPortal(new THREE.Vector3(0, 3.6, 57));
    finalPortal.setOnEnter(() => {
      this.gameComplete = true;
    });
  }
}
